/* eslint-disable */
// Session 55 · Alpha Launch — Reacceptance Modal.
// Shown by AppShell whenever /api/auth/me returns
// `legal_status.needs_reacceptance === true`. Blocks all app content behind
// an overlay until the user re-accepts both docs. Then POSTs to
// /api/legal/accept and refreshes the auth context so the modal disappears.

import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function ReacceptanceModal() {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const [current, setCurrent] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const needs = user?.legal_status?.needs_reacceptance === true;
  // Session 57 · Suppress on /first-login where the wizard already collects
  // Terms + Privacy acceptance in Step 3 (avoids two overlapping modals).
  const suppress = location.pathname === "/first-login" || user?.must_change_password === true;

  useEffect(() => {
    if (!needs || suppress) return;
    api.get("/legal/current").then((r) => setCurrent(r.data.documents)).catch(() => {});
  }, [needs, suppress]);

  if (!user || !needs || suppress || !current?.terms || !current?.privacy) return null;

  const submit = async () => {
    if (!acceptTerms || !acceptPrivacy) return;
    setErr(""); setBusy(true);
    try {
      await api.post("/legal/accept", {
        terms_version: current.terms.version,
        privacy_version: current.privacy.version,
        context: "reacceptance",
      });
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unable to record acceptance");
    } finally {
      setBusy(false);
    }
  };

  const stale = user.legal_status?.stale || [];
  const bothStale = stale.includes("terms") && stale.includes("privacy");

  return (
    <div className="legal-reaccept-overlay" data-testid="reacceptance-modal">
      <div className="legal-reaccept-card" role="dialog" aria-modal="true"
        aria-labelledby="reaccept-title">
        <h2 id="reaccept-title" className="legal-reaccept-title">
          {bothStale ? "Updated Legal Documents" :
             stale.includes("terms") ? "Updated Terms of Service" :
             "Updated Privacy Policy"}
        </h2>
        <p className="legal-reaccept-copy">
          {bothStale
            ? `Métier has published updated legal documents. Please review and accept the Terms of Service (v${current.terms.version}) and Privacy Policy (v${current.privacy.version}) to continue using the platform.`
            : stale.includes("terms")
              ? `Our Terms of Service have been updated (v${current.terms.version}, effective ${current.terms.effective_date}). Please review and accept to continue.`
              : `Our Privacy Policy has been updated (v${current.privacy.version}, effective ${current.privacy.effective_date}). Please review and accept to continue.`}
        </p>

        <div className="legal-reaccept-checks">
          <label>
            <input type="checkbox" checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              data-testid="reaccept-terms" />
            <span>
              I have read and agree to the{" "}
              <Link to="/legal/terms" target="_blank" rel="noopener"
                data-testid="reaccept-terms-link">
                Terms of Service (v{current.terms.version})
              </Link>.
            </span>
          </label>
          <label>
            <input type="checkbox" checked={acceptPrivacy}
              onChange={(e) => setAcceptPrivacy(e.target.checked)}
              data-testid="reaccept-privacy" />
            <span>
              I acknowledge the{" "}
              <Link to="/legal/privacy" target="_blank" rel="noopener"
                data-testid="reaccept-privacy-link">
                Privacy Policy (v{current.privacy.version})
              </Link>.
            </span>
          </label>
        </div>

        {err && <div className="legal-error" data-testid="reaccept-error">{err}</div>}

        <div className="legal-reaccept-actions">
          <button
            type="button"
            className="legal-btn-primary"
            onClick={submit}
            disabled={busy || !acceptTerms || !acceptPrivacy}
            data-testid="reaccept-submit"
          >
            {busy ? "Recording…" : "Accept & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
